<template>
  <div class="app-home">
    <section class="app-home__hero">
      <LogoTile :branding="branding" :alt="tenantName" :size="40" />
      <div class="app-home__hero-text">
        <h1 class="app-home__title">{{ welcomeTitle }}</h1>
        <p v-if="welcomeText" class="app-home__subtitle">{{ welcomeText }}</p>
      </div>
    </section>

    <div v-if="visibleTiles.length" class="app-home__grid">
      <button
        v-for="tile in visibleTiles"
        :key="tile.route"
        type="button"
        class="app-home__tile"
        @click="onTileClick(tile)"
      >
        <v-icon size="28" color="primary" class="app-home__tile-icon">{{ tile.icon }}</v-icon>
        <span class="app-home__tile-title">{{ tile.title }}</span>
        <span v-if="tile.description" class="app-home__tile-desc">{{ tile.description }}</span>
      </button>
    </div>
    <p v-else class="app-home__empty">No apps are available yet.</p>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { navigateToUrl } from "single-spa";
import { useSessionStore } from "@/stores/session";
import { isTileRoutePermitted } from "@/nav/routeGuard";
import LogoTile from "@/shell/LogoTile.vue";
import type { TenantHomeTile } from "@/tenant/types";

/**
 * Rendered at `/` when `tenant.homePage?.enabled` (App.vue), Appendix A7.
 * Tiles are pre-validated backend-side to sit under one of the app's module
 * prefixes (and inside `allowedItems` when set) — `isTileRoutePermitted`
 * re-checks that against the CALLER's actually-resolved modules, so a tile
 * configured before a module/allowedItems change was tightened never renders
 * a dead or now-forbidden link.
 */
export default defineComponent({
  name: "AppHome",
  components: { LogoTile },
  setup() {
    const session = useSessionStore();

    const tenantName = computed(() => session.tenant?.name || "MetaShip");
    const branding = computed(() => session.tenant?.branding ?? null);

    const homePage = computed(() => session.tenant?.homePage ?? null);
    const welcomeTitle = computed(() => homePage.value?.welcomeTitle || tenantName.value);
    const welcomeText = computed(() => homePage.value?.welcomeText || null);

    const visibleTiles = computed<TenantHomeTile[]>(() => {
      const tiles = homePage.value?.tiles ?? [];
      const modules = session.tenantModules;
      return tiles.filter((tile) => isTileRoutePermitted(tile.route, modules));
    });

    function onTileClick(tile: TenantHomeTile): void {
      navigateToUrl(tile.route);
      const [path, query] = tile.route.split("?");
      window.dispatchEvent(
        new CustomEvent("metaship:navigate", {
          detail: { path, query: Object.fromEntries(new URLSearchParams(query || "")) },
        }),
      );
    }

    return { tenantName, branding, welcomeTitle, welcomeText, visibleTiles, onTileClick };
  },
});
</script>

<style scoped>
.app-home {
  max-width: 1040px;
  margin: 0 auto;
  padding: 32px 24px 40px;
}

.app-home__hero {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
}
.app-home__title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
}
.app-home__subtitle {
  font-size: 13px;
  color: rgba(var(--v-theme-on-background), 0.7);
  margin: 4px 0 0;
}

.app-home__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.app-home__tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid rgba(var(--v-theme-on-background), 0.08);
  background: rgb(var(--v-theme-foreground));
  cursor: pointer;
  text-align: left;
  transition:
    transform 120ms ease,
    box-shadow 120ms ease,
    border-color 120ms ease;
  font-family: inherit;
}
.app-home__tile:hover,
.app-home__tile:focus-visible {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.16);
  transform: translateY(-1px);
  outline: none;
}
.app-home__tile-icon {
  margin-bottom: 2px;
}
.app-home__tile-title {
  font-size: 13px;
  font-weight: 700;
}
.app-home__tile-desc {
  font-size: 12px;
  color: rgba(var(--v-theme-on-background), 0.65);
  line-height: 1.4;
}

.app-home__empty {
  font-size: 13px;
  color: rgba(var(--v-theme-on-background), 0.65);
}
</style>
